/**
 * ApprovalsContext - Data store for page approval data
 *
 * This provider follows the pattern established in AnnotationsProvider and
 * documented in patterns/component-architecture.md:
 * - Separate read and write contexts to prevent unnecessary re-renders
 * - Server data managed by Apollo GraphQL (useQueryInterop)
 * - UI state managed by React Context
 *
 * Usage:
 *   const { approvalRequest, loading } = useApprovals();
 *   const { startApproval, updateApprovalStatus } = useApprovalsDispatch();
 */

import React, {
	createContext,
	useCallback,
	useContext,
	useEffect,
	useMemo,
	useRef,
	useState,
	type FC,
	type ReactNode,
} from 'react';
import { graphql } from 'react-relay';

import { useQueryInterop } from '@confluence/apollo-relay-interop/entry-points/useQueryInterop.ts';
import { useMutationInterop } from '@confluence/apollo-relay-interop/entry-points/useMutationInterop.ts';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';
import { expValEquals } from '@confluence/feature-experiments';
import { fg } from '@confluence/feature-gating';
import { usePageContentId, usePageState } from '@confluence/page-context';
import { useGetPageMode } from '@confluence/page-utils/entry-points/useGetPageMode.ts';
import {
	PageMode,
	CURRENT_STATUS,
	DRAFT_STATUS,
} from '@confluence/page-utils/entry-points/enums.ts';
import { useCanPerformContentOperation } from '@confluence/entity-operations/entry-points/entityOperationsHelpers.ts';
import { OperationTypes } from '@confluence/entity-operations/entry-points/operationsTypes.ts';
import { markErrorAsHandled } from '@confluence/graphql/entry-points/markErrorAsHandled.ts';
import { getMonitoringClient } from '@confluence/monitoring/entry-points/getMonitoringClient.ts';
import { Attribution } from '@confluence/error-boundary/entry-points/Attribution.ts';
import type { ConfluenceWorkflowApplicationChanged } from '@confluence/graphql/entry-points/subscriptionTypes.ts';
import { useRightSidebarContext } from '@confluence/page-layout-context';
import { useSubscriptionEvent } from '@confluence/graphql/entry-points/useSubscriptionEvent.ts';
import {
	QuickReloadSubscriber,
	ReloadType,
} from '@confluence/quick-reload/entry-points/subscription.ts';

import { APPROVALS_PANE_ID, openApprovalsPaneForContext } from './approvalsPaneSidebar.ts';
import { ApprovalCtaFlag } from './ApprovalCtaFlag.tsx';
import {
	consumeLiveDocApprovalRedirectIntent,
	setLiveDocApprovalRedirectIntent,
} from './liveDocApprovalRedirect.ts';
import type {
	ApprovalsContextCreateWorkflowApplicationMutation$data,
	ApprovalsContextCreateWorkflowApplicationMutation$variables,
} from './__generated__/ApprovalsContextCreateWorkflowApplicationMutation.graphql.ts';
import type {
	ApprovalsContextCreateWorkflowReviewMutation$data,
	ApprovalsContextCreateWorkflowReviewMutation$variables,
} from './__generated__/ApprovalsContextCreateWorkflowReviewMutation.graphql.ts';
import type {
	ApprovalsContextUpdateWorkflowApplicationMutation$data,
	ApprovalsContextUpdateWorkflowApplicationMutation$variables,
	GraphQLContentStatus,
} from './__generated__/ApprovalsContextUpdateWorkflowApplicationMutation.graphql.ts';
import type {
	ApprovalsContextUpdateReviewerDecisionMutation$data,
	ApprovalsContextUpdateReviewerDecisionMutation$variables,
	ConfluenceReviewerDecisionState,
} from './__generated__/ApprovalsContextUpdateReviewerDecisionMutation.graphql.ts';
import type {
	ApprovalsContextUpdateWorkflowReviewMutation$data,
	ApprovalsContextUpdateWorkflowReviewMutation$variables,
} from './__generated__/ApprovalsContextUpdateWorkflowReviewMutation.graphql.ts';
import type {
	ApprovalsContextRemoveContentApprovalReviewerMutation$data,
	ApprovalsContextRemoveContentApprovalReviewerMutation$variables,
} from './__generated__/ApprovalsContextRemoveContentApprovalReviewerMutation.graphql.ts';
import type {
	ApprovalsContextCreateWorkflowApplicationWithReviewMutation$data,
	ApprovalsContextCreateWorkflowApplicationWithReviewMutation$variables,
} from './__generated__/ApprovalsContextCreateWorkflowApplicationWithReviewMutation.graphql.ts';
import type {
	ApprovalsContextApprovalsDataQuery$data,
	ApprovalsContextApprovalsDataQuery$variables,
	ConfluenceWorkflowReviewStatus,
	ConfluencePrincipalType as PrincipalType,
	ConfluenceReviewerDecisionState as ReviewerDecisionStatus,
} from './__generated__/ApprovalsContextApprovalsDataQuery.graphql.ts';

const DRAFT_CONTENT_STATUS: GraphQLContentStatus = 'DRAFT';
const CURRENT_CONTENT_STATUS: GraphQLContentStatus = 'CURRENT';

// errorType values returned by confluence_createContentApproval that represent
// known, expected failure conditions. These are marked as handled so they do not
// surface as unexpected errors in monitoring.
const EXPECTED_CREATE_APPROVAL_ERROR_TYPES = new Set(['BAD_REQUEST', 'ACCESS_DENIED', 'NOT_FOUND']);

/**
 * Shared error classification for approval mutation responses.
 * Classifies the error as handled (expected) or reports to monitoring (unexpected),
 * then throws so the caller can surface it to the UI.
 */
function handleApprovalMutationError(
	errors: ReadonlyArray<{
		readonly message: string | null | undefined;
		readonly extensions:
			| {
					readonly errorType: string | null | undefined;
					readonly statusCode: number | null | undefined;
			  }
			| null
			| undefined;
	}>,
	fallbackMessage: string,
): never {
	const firstError = errors[0];
	const errorMessage = firstError?.message || fallbackMessage;
	const errorType = firstError?.extensions?.errorType;
	const error = Object.assign(new Error(errorMessage), {
		extensions: firstError?.extensions,
	});

	if (errorType && EXPECTED_CREATE_APPROVAL_ERROR_TYPES.has(errorType)) {
		markErrorAsHandled(error);
	} else {
		getMonitoringClient().submitError(error, {
			attribution: Attribution.ADMIN_EXPERIENCE,
		});
	}

	throw error;
}

// GraphQL Mutations - AGG Ready
// Mutation: Create Workflow Application
const createWorkflowApplicationMutation = graphql`
	mutation ApprovalsContextCreateWorkflowApplicationMutation(
		$cloudId: ID!
		$createWorkflowApplicationRequestInput: ConfluenceCreateWorkflowApplicationRequestInput!
	) {
		confluence_createWorkflowApplication(
			cloudId: $cloudId
			createWorkflowApplicationRequestInput: $createWorkflowApplicationRequestInput
		) {
			success
			errors {
				message
				extensions {
					statusCode
					errorType
				}
			}
			workflowApplication {
				id
			}
			workflowStatus {
				id
				allowsReviews
				displayProperties {
					name
					description
					color
					icon
				}
			}
		}
	}
`;

// Mutation: Create Workflow Review
const createWorkflowReviewMutation = graphql`
	mutation ApprovalsContextCreateWorkflowReviewMutation(
		$cloudId: ID!
		$createContentApprovalRequestInput: ConfluenceCreateContentApprovalRequestInput!
	) {
		confluence_createContentApproval(
			cloudId: $cloudId
			createContentApprovalRequestInput: $createContentApprovalRequestInput
		) {
			success
			errors {
				message
				extensions {
					statusCode
					errorType
				}
			}
			contentApproval {
				id
				workflowApplicationId
				reviewStatus
				comment
				reviewers {
					principalType
					principalId
					isRequired
					user {
						id
						displayName
						photos {
							isPrimary
							value
						}
						emails {
							isPrimary
							value
						}
					}
				}
			}
		}
	}
`;

// Mutation: Update Workflow Application
const updateWorkflowApplicationMutation = graphql`
	mutation ApprovalsContextUpdateWorkflowApplicationMutation(
		$cloudId: ID!
		$updateWorkflowApplicationRequestInput: ConfluenceUpdateWorkflowApplicationRequestInput!
	) {
		confluence_updateWorkflowApplication(
			cloudId: $cloudId
			updateWorkflowApplicationRequestInput: $updateWorkflowApplicationRequestInput
		) {
			success
			errors {
				message
				extensions {
					statusCode
					errorType
				}
			}
		}
	}
`;

// Mutation: Update Reviewer Decision (Approve/Deny)
const updateReviewerDecisionMutation = graphql`
	mutation ApprovalsContextUpdateReviewerDecisionMutation(
		$cloudId: ID!
		$confluenceUpdateReviewerDecisionInput: ConfluenceUpdateReviewerDecisionInput!
	) {
		confluence_updateReviewerDecision(
			cloudId: $cloudId
			confluenceUpdateReviewerDecisionInput: $confluenceUpdateReviewerDecisionInput
		) {
			success
			errors {
				message
				extensions {
					statusCode
					errorType
				}
			}
		}
	}
`;

// Mutation: Update Workflow Review (add/remove reviewers, update status)
const updateWorkflowReviewMutation = graphql`
	mutation ApprovalsContextUpdateWorkflowReviewMutation(
		$cloudId: ID!
		$updateContentApprovalGraphQLInput: ConfluenceUpdateContentApprovalRequestInput!
	) {
		confluence_updateContentApproval(
			cloudId: $cloudId
			updateContentApprovalGraphQLInput: $updateContentApprovalGraphQLInput
		) {
			success
			errors {
				message
				extensions {
					statusCode
					errorType
				}
			}
			contentApproval {
				id
				reviewStatus
				comment
				reviewers {
					principalType
					principalId
					isRequired
					user {
						id
						displayName
						photos {
							isPrimary
							value
						}
						emails {
							isPrimary
							value
						}
					}
				}
			}
		}
	}
`;

// Mutation: Resolve Approval Agent
const resolveApprovalAgentMutation = graphql`
	mutation ApprovalsContextResolveApprovalAgentMutation($cloudId: ID!) {
		confluence_resolveApprovalAgent(cloudId: $cloudId) {
			success
			agent {
				id
			}
			errors {
				message
			}
		}
	}
`;

// Mutation: Create Workflow Application With Review (combined)
const createWorkflowApplicationWithReviewMutation = graphql`
	mutation ApprovalsContextCreateWorkflowApplicationWithReviewMutation(
		$cloudId: ID!
		$createWorkflowApplicationWithReviewRequestInput: ConfluenceCreateContentApprovalRequestInput!
	) {
		confluence_createWorkflowApplicationWithReview(
			cloudId: $cloudId
			createWorkflowApplicationWithReviewRequestInput: $createWorkflowApplicationWithReviewRequestInput
		) {
			success
			errors {
				message
				extensions {
					statusCode
					errorType
				}
			}
			workflowApplication {
				id
			}
			workflowReviewWithDecisions {
				id
			}
			workflowStatus {
				id
				allowsReviews
				displayProperties {
					name
					description
					color
					icon
				}
			}
		}
	}
`;

// Mutation: Remove Content Approval Reviewer
const removeContentApprovalReviewerMutation = graphql`
	mutation ApprovalsContextRemoveContentApprovalReviewerMutation(
		$cloudId: ID!
		$removeContentApprovalReviewerInput: ConfluenceRemoveContentApprovalReviewerInput!
	) {
		confluence_removeContentApprovalReviewer(
			cloudId: $cloudId
			removeContentApprovalReviewerInput: $removeContentApprovalReviewerInput
		) {
			success
			errors {
				message
				extensions {
					statusCode
					errorType
				}
			}
		}
	}
`;

// GraphQL Query - Combined: active workflow application + available workflow statuses
// Single query for preloading and runtime; fetches both confluence_getActiveWorkflowApplication
// and confluence_contentApprovalStatuses (transitionType: null returns all statuses)
const approvalsDataQuery = graphql`
	query ApprovalsContextApprovalsDataQuery(
		$cloudId: ID!
		$contentId: ID!
		$contentStatus: String!
		$otherContentStatus: String!
		$transitionType: ConfluenceApprovalTransitionType
		$spaceKey: String!
	) {
		space(key: $spaceKey) {
			id
			spaceTypeSettings {
				enabledFeatures {
					isContentApprovalsEnabled
				}
			}
		}
		content(id: $contentId) {
			nodes {
				status
				version {
					number
				}
			}
		}
		confluence_contentApprovalStatuses(cloudId: $cloudId, transitionType: $transitionType) {
			id
			allowsReviews
			displayProperties {
				name
				color
			}
		}
		confluence_getActiveWorkflowApplicationForOtherStatus: confluence_getActiveWorkflowApplication(
			cloudId: $cloudId
			contentId: $contentId
			contentStatus: $otherContentStatus
			workflowId: "1"
			fetchWorkflowReview: true
		) {
			success
			errors {
				message
				extensions {
					statusCode
					errorType
				}
			}
			workflowReviewWithDecisions {
				id
				reviewStatus
			}
		}
		confluence_getActiveWorkflowApplication(
			cloudId: $cloudId
			contentId: $contentId
			contentStatus: $contentStatus
			workflowId: "1"
			fetchWorkflowReview: true
		) {
			success
			errors {
				message
				extensions {
					statusCode
					errorType
				}
			}
			workflowApplication {
				id
				workflowId
				contentId
				contentVersion
				contentStatus
				workflowStatusId
				isActive
				creator {
					id
					displayName
					isActive
					photos {
						value
						isPrimary
					}
				}
				lastModificationDate {
					value
				}
				modifier {
					id
					displayName
					isActive
					photos {
						value
						isPrimary
					}
				}
			}
			workflowReviewWithDecisions {
				id
				creatorAaid
				creationDate {
					value
				}
				reviewStatus
				reviewComment
				reviewLastModified {
					value
				}
				creator {
					id
					displayName
					isActive
					photos {
						value
						isPrimary
					}
				}
				reviewerDecisions {
					decisionId
					reviewerId
					required
					principalType
					principalId
					reviewerDecisionStatus
					comment
					lastModifiedDate {
						value
					}
					user {
						id
						displayName
						isActive
						photos {
							isPrimary
							value
						}
					}
				}
			}
		}
	}
`;

/**
 * Data structure for user information in content state updates.
 * Used by UserUpdateInfo component and ContentStateInfoPopup.
 * This is the canonical shape for user metadata in content state contexts.
 */
export type UserUpdateInfoData = {
	displayName: string;
	avatarUrl?: string;
	userId: string;
};

export type ApprovalStatus = ConfluenceWorkflowReviewStatus;

export type Approver = {
	decisionId: string;
	reviewerId: string;
	principalId: string;
	principalType: PrincipalType;
	name: string;
	avatarUrl?: string;
	email?: string;
	status: ConfluenceReviewerDecisionState;
	decisionComment?: string;
	isRequired: boolean;
	responseLastModifiedDate?: string;
};

/** Workflow application data from the active workflow application query */
export type ApprovalRequestApplication = {
	contentId: string;
	contentVersion: number;
	contentStatus: string;
	applicationId: string;
	isActive: boolean;
	workflowStatusId: string;
	applicationLastModifiedBy?: UserUpdateInfoData;
	applicationLastModifiedDate?: string;
};

/** Review data (requestor, review metadata, approvers). Null when there is an application but no review yet. */
export type ApprovalRequestReview = {
	reviewId: string;
	reviewCreationDate: string;
	requestorId: string;
	requestorName: string;
	requestorAvatarUrl?: string;
	reviewLastModifiedDate: string;
	reviewComment?: string;
	reviewStatus: ApprovalStatus;
	approvers: Approver[];
};

export type ApprovalRequest = {
	application: ApprovalRequestApplication;
	review: ApprovalRequestReview | null;
};

export type WorkflowStatus = {
	id: string;
	name: string;
	color?: string;
	allowsReviews: boolean;
};

export type ApprovalsContextValue = {
	// Query data
	approvalRequest: ApprovalRequest | null;
	approvalRequestLoading: boolean;

	// Available workflow statuses
	availableWorkflowStatuses: WorkflowStatus[];

	// Content identifiers (for analytics and cross-component reference)
	contentId: string;
	contentStatus: GraphQLContentStatus;

	// Computed values
	isCurrentUserRequestor: boolean;
	isCurrentUserApprover: boolean;
	currentUserApprover: Approver | null;
	approvalProgress: { current: number; total: number };

	// Permissions (pushed from EditorContentStateComponent)
	hasEditPermission: boolean;

	/**
	 * True when the draft is locked for editing: content is draft and approval is in progress
	 * (PENDING), approved (APPROVED), or denied (DENIED). Used to prevent Rovo from editing
	 * the page in this state (via isViewMode).
	 */
	isDraftLockedForEditing: boolean;

	/**
	 * The review status of the opposite content mode (Edit ↔ View).
	 * Used to show a warning when the other mode has an active, pending review.
	 */
	otherModeReviewStatus: ApprovalStatus | null;

	/** Whether the space-level content approvals setting is enabled */
	isContentApprovalsEnabled: boolean;

	/**
	 * Set when the user has just initiated a live-doc → page conversion specifically to start
	 * an approval. The value is the contentId of the live doc the user clicked from. Survives
	 * the convert-to-page redirect via a sessionStorage mirror that is hydrated on mount.
	 *
	 * Consumed by `PublishModalMountPoint` (to auto-open the publish modal in convert-to-page
	 * mode) and by `ApprovalsProviderComponent` itself (to auto-open the approvals pane on the
	 * resulting page after redirect).
	 *
	 * Always `null` when the `confluence_approvals_beta` gate is off — the setter is a no-op
	 * in that case.
	 */
	pendingLiveDocApprovalContentId: string | null;
};

export type StartApprovalVariables = {
	approvers: Array<{
		principalId: string;
		principalType: PrincipalType;
		isRequired: boolean;
	}>;
	message?: string;
};

export type UpdateWorkflowApplicationVariables = {
	contentId: string;
	contentStatus: string;
	version: number;
	workflowStatusId: string;
	isActive: boolean;
};

export type UpdateApprovalStatusVariables = {
	reviewerId: string;
	status: Extract<ReviewerDecisionStatus, 'REVIEWER_APPROVED' | 'REVIEWER_DENIED'>;
	comment?: string;
};

export type AddApproverVariables = {
	workflowApplicationId: string;
	contentApprovalReviewId: string;
	approvers: Array<{
		principalId: string;
		principalType: PrincipalType;
		isRequired: boolean;
	}>;
};

export type RemoveApproverVariables = {
	principalId: string;
};

export type CancelApprovalVariables = {
	workflowApplicationId: string;
	contentApprovalReviewId: string;
};

export type CreateWorkflowApplicationResult = {
	applicationId: string | null | undefined;
	workflowStatusId: string;
	name: string;
	allowsReviews: boolean;
};

export type StartApprovalResult = {
	id: string;
	reviewStatus: ConfluenceWorkflowReviewStatus;
	workflowApplicationId: string | null | undefined;
	comment: string | null | undefined;
};

export type ApprovalsDispatchContextValue = {
	// Mutations
	createWorkflowApplication: (status: WorkflowStatus) => Promise<CreateWorkflowApplicationResult>;
	cancelWorkflowApplication: (
		variables?: Omit<UpdateWorkflowApplicationVariables, 'isActive'>,
	) => Promise<void>;
	clearApprovalRequestOptimistically: () => void;
	startApproval: (variables: StartApprovalVariables) => Promise<StartApprovalResult | undefined>;
	updateApprovalStatus: (variables: UpdateApprovalStatusVariables) => Promise<void>;
	addApprover: (variables: AddApproverVariables) => Promise<void>;
	removeApprover: (variables: RemoveApproverVariables) => Promise<void>;
	cancelApproval: (variables: CancelApprovalVariables) => Promise<void>;
	resolveApprovalAgent: () => Promise<string>;
	startApprovalCombined: (
		variables: StartApprovalVariables,
	) => Promise<StartApprovalResult | undefined>;

	/**
	 * Mark that the user just initiated a live-doc → page conversion specifically to start
	 * an approval. Persists across the redirect via sessionStorage. No-op when the
	 * `confluence_approvals_beta` gate is off.
	 *
	 * After dispatch, the live-doc page's `PublishModalMountPoint` will auto-open the
	 * publish modal in convert-to-page mode; once the page redirect completes, the new
	 * page's `ApprovalsProvider` will auto-open the approvals pane and clear the intent.
	 */
	requestLiveDocApprovalConversion: (contentId: string) => void;

	/**
	 * Clear the pending live-doc approval conversion intent from React state only.
	 * sessionStorage is intentionally NOT cleared — it must survive the convert-to-page
	 * redirect so ApprovalsProvider on the destination page can consume it and auto-open
	 * the approvals pane. Called when the publish modal closes (cancel or publish CTA click)
	 * so the React state resets to null, allowing a retry without a spurious auto-reopen.
	 */
	clearLiveDocApprovalConversion: () => void;
};

// Default context values
const defaultContextValue: ApprovalsContextValue = {
	approvalRequest: null,
	approvalRequestLoading: false,
	availableWorkflowStatuses: [],
	contentId: '',
	contentStatus: CURRENT_CONTENT_STATUS,
	isCurrentUserRequestor: false,
	isCurrentUserApprover: false,
	currentUserApprover: null,
	approvalProgress: { current: 0, total: 0 },
	hasEditPermission: false,
	isDraftLockedForEditing: false,
	otherModeReviewStatus: null,
	isContentApprovalsEnabled: false,
	pendingLiveDocApprovalContentId: null,
};

const defaultDispatchValue: ApprovalsDispatchContextValue = {
	createWorkflowApplication: async () => {
		throw new Error('createWorkflowApplication not implemented');
	},
	cancelWorkflowApplication: async () => {
		throw new Error('cancelWorkflowApplication not implemented');
	},
	clearApprovalRequestOptimistically: () => {},
	startApproval: async () => {
		throw new Error('startApproval not implemented');
	},
	updateApprovalStatus: async () => {
		throw new Error('updateApprovalStatus not implemented');
	},
	addApprover: async () => {
		throw new Error('addApprover not implemented');
	},
	removeApprover: async () => {
		throw new Error('removeApprover not implemented');
	},
	cancelApproval: async () => {
		throw new Error('cancelApproval not implemented');
	},
	resolveApprovalAgent: async () => '',
	startApprovalCombined: async () => undefined,
	requestLiveDocApprovalConversion: () => {},
	clearLiveDocApprovalConversion: () => {},
};

// Create contexts
export const ApprovalsContext = createContext<ApprovalsContextValue>(defaultContextValue);
ApprovalsContext.displayName = 'ApprovalsContext';

export const ApprovalsDispatchContext =
	createContext<ApprovalsDispatchContextValue>(defaultDispatchValue);
ApprovalsDispatchContext.displayName = 'ApprovalsDispatchContext';

// Provider props
type ApprovalsProviderProps = {
	children: ReactNode;
	/** Optional contentId override. When provided, bypasses usePageContentId() — useful when
	 * rendering outside the page context tree (e.g. modals with contentId as a prop). */
	contentId?: string;
};

type ApprovalsProviderComponentProps = ApprovalsProviderProps & {
	contentId: string;
	spaceKey: string;
};

/**
 * DefaultApprovalsProvider - Returns default/empty approval context
 */
const DefaultApprovalsProvider: FC<ApprovalsProviderProps> = ({ children }) => (
	<ApprovalsContext.Provider value={defaultContextValue}>
		<ApprovalsDispatchContext.Provider value={defaultDispatchValue}>
			{children}
		</ApprovalsDispatchContext.Provider>
	</ApprovalsContext.Provider>
);

/**
 * ApprovalsProvider - Safe wrapper with validation and kill switch
 * @example
 * ```tsx
 * <ApprovalsProvider>
 *   <BylineApprovalStatus />
 *   <ApprovalsPane />
 * </ApprovalsProvider>
 * ```
 */
export const ApprovalsProvider: FC<ApprovalsProviderProps> = ({
	children,
	contentId: contentIdProp,
}) => {
	const { cloudId } = useSessionData();
	const [pageContentId] = usePageContentId();
	const [{ spaceKey }] = usePageState();
	const contentId = contentIdProp || pageContentId;

	if (!cloudId || !contentId || !spaceKey || !fg('confluence_approvals_fg')) {
		return <DefaultApprovalsProvider>{children}</DefaultApprovalsProvider>;
	}

	return (
		<ApprovalsProviderComponent contentId={contentId} spaceKey={spaceKey}>
			{children}
		</ApprovalsProviderComponent>
	);
};

/**
 * This provider:
 * 1. Fetches approval data using GraphQL query
 * 2. Exposes data through read context (ApprovalsContext)
 * 3. Exposes mutations through write context (ApprovalsDispatchContext)
 * 4. Handles cache updates and refetching after mutations
 */
const ApprovalsProviderComponent: FC<ApprovalsProviderComponentProps> = ({
	children,
	contentId,
	spaceKey,
}) => {
	const { cloudId, userId } = useSessionData();
	const [{ contentType }] = usePageState();
	const pageMode = useGetPageMode();
	const contentStatus: GraphQLContentStatus =
		pageMode === PageMode.EDIT ? DRAFT_CONTENT_STATUS : CURRENT_CONTENT_STATUS;
	// The content status that is opposite of the current status
	const otherContentStatus =
		contentStatus === DRAFT_CONTENT_STATUS ? CURRENT_CONTENT_STATUS : DRAFT_CONTENT_STATUS;

	const rightSidebarContext = useRightSidebarContext();
	const { close: closeRightSidebar, open: openRightSidebar } = rightSidebarContext;

	const [isOptimisticallyCancelled, setIsOptimisticallyCancelled] = useState(false);
	/**
	 * Live-doc → page approval conversion intent. 
	 * We use this to track the contentId of the live-doc that the user wants to convert to a page.
	 */
	const [pendingLiveDocApprovalContentId, setPendingLiveDocApprovalContentId] = useState<
		string | null
	>(null);
	const [optimisticWorkflowStatus, setOptimisticWorkflowStatus] = useState<WorkflowStatus | null>(
		null,
	);

	// Tracks whether a cancelWorkflowApplication mutation is currently in flight.
	// Used to prevent the workflowApplicationChanged subscription from resetting
	// isOptimisticallyCancelled before the cancel mutation has completed on the backend,
	// which would cause a flash of stale workflow status data.
	const isCancellationInFlightRef = useRef(false);

	// Track whether we've received data for the current content/mode pair. This ensures
	// approvalRequestLoading stays true from the moment contentId or contentStatus changes until
	// fresh data has actually arrived — even if useQueryInteropRelay's loading flag flips true→false
	// within a single React batch (e.g. when the Relay store has cached normalized data).
	const currentContentCacheKey = `${contentId}:${contentStatus}`;
	const lastSettledContentCacheKeyRef = useRef<string | null>(null);
	const shouldForceNetworkFetch = lastSettledContentCacheKeyRef.current !== currentContentCacheKey;

	// Reset optimistic state when navigating to a different page or switching content status (View ↔ Edit).
	useEffect(() => {
		setIsOptimisticallyCancelled(false);
		setOptimisticWorkflowStatus(null);
	}, [contentId, contentStatus]);


	const canEdit = useCanPerformContentOperation({
		contentId,
		operation: OperationTypes.UPDATE,
		status: contentStatus === CURRENT_CONTENT_STATUS ? CURRENT_STATUS : DRAFT_STATUS,
	});
	const hasEditPermission = canEdit ?? false;

	// Query for available workflow statuses and active workflow application
	const {
		data: approvalsData,
		loading: approvalsLoading,
		refetch: refetchApprovalsData,
	} = useQueryInterop<
		ApprovalsContextApprovalsDataQuery$data,
		ApprovalsContextApprovalsDataQuery$variables
	>(approvalsDataQuery, {
		variables: {
			cloudId,
			contentId,
			contentStatus,
			transitionType: null, // null returns all statuses
			otherContentStatus,
			spaceKey,
		},
		...(shouldForceNetworkFetch ? { fetchPolicy: 'network-only' } : {}),
		skip: !cloudId || !contentId || !expValEquals('confluence_approvals_eap', 'isEnabled', true),
	});

	const refetchVariables = useMemo(
		() =>
			({
				cloudId,
				contentId,
				contentStatus,
				transitionType: null,
				otherContentStatus,
				spaceKey,
				cacheBuster: '',
			}) as ApprovalsContextApprovalsDataQuery$variables,
		[cloudId, contentId, contentStatus, otherContentStatus, spaceKey],
	);

	// Detect when approvalsData belongs to a previously-viewed page. useQueryInteropRelay keeps old
	// data in state while a refetch with new variables is in flight, so without this guard the UI
	// would render Page A's fully-interactive workflow/review data while viewing Page B.
	const isDataStale = useMemo(() => {
		const responseContentId =
			approvalsData?.confluence_getActiveWorkflowApplication?.workflowApplication?.contentId;
		// If the response contains a workflow application for a different page, data is stale.
		// When responseContentId is null/undefined/empty (no active workflow or falsy defaults),
		// there is nothing stale to hide.
		return !!responseContentId && responseContentId !== contentId;
	}, [approvalsData, contentId]);

	if (!approvalsLoading && !isDataStale) {
		lastSettledContentCacheKeyRef.current = currentContentCacheKey;
	}

	const isAwaitingFreshData = lastSettledContentCacheKeyRef.current !== currentContentCacheKey;

	const availableWorkflowStatuses = useMemo(() => {
		if (isDataStale || !approvalsData?.confluence_contentApprovalStatuses) return [];
		return approvalsData.confluence_contentApprovalStatuses.reduce<WorkflowStatus[]>(
			(acc, status) => {
				if (status === null || status === undefined) return acc;
				acc.push({
					id: status.id || '',
					name: status.displayProperties?.name || '',
					allowsReviews: status.allowsReviews || false,
					color: status.displayProperties?.color || undefined,
				});
				return acc;
			},
			[],
		);
	}, [isDataStale, approvalsData]);

	const contentVersion = useMemo(() => {
		const fromWorkflow =
			approvalsData?.confluence_getActiveWorkflowApplication?.workflowApplication?.contentVersion;
		if (fromWorkflow !== undefined && fromWorkflow !== null) {
			return fromWorkflow;
		}
		return approvalsData?.content?.nodes?.[0]?.version?.number ?? 0;
	}, [approvalsData]);

	const isContentApprovalsEnabled = useMemo(
		() =>
			approvalsData?.space?.spaceTypeSettings?.enabledFeatures?.isContentApprovalsEnabled ?? false,
		[approvalsData],
	);

	/**
	 * Live-doc → page approval auto-open effect.
	 *
	 * After a successful convert-to-page redirect, the new page mounts this provider with the
	 * intent still set in sessionStorage. When we land on the CURRENT (published) content in
	 * VIEW mode and the intent matches this contentId, atomically consume the persisted intent,
	 * clear the React state, and open the approvals pane.
	 */
	useEffect(() => {
		if (!fg('confluence_approvals_beta')) return;
		if (contentStatus !== CURRENT_CONTENT_STATUS) return;
		if (pageMode !== PageMode.VIEW) return;
		if (approvalsLoading) return; // wait for data before consuming

		const intent = consumeLiveDocApprovalRedirectIntent();
		if (!intent || intent.contentId !== contentId) return;

		setPendingLiveDocApprovalContentId(null);
		if (isContentApprovalsEnabled) {
			// eslint-disable-next-line @atlassian/perf-linting/no-chain-state-updates
			openApprovalsPaneForContext({
				contentId,
				contentStatus: CURRENT_CONTENT_STATUS,
				rightSidebarContext: { open: openRightSidebar },
			});
		}
	}, [contentId, openRightSidebar, contentStatus, pageMode, approvalsLoading, isContentApprovalsEnabled]);

	useEffect(() => {
		if (
			optimisticWorkflowStatus &&
			approvalsData?.confluence_getActiveWorkflowApplication?.workflowApplication
				?.workflowStatusId === optimisticWorkflowStatus.id
		) {
			// eslint-disable-next-line @atlassian/perf-linting/no-chain-state-updates
			setOptimisticWorkflowStatus(null);
		}
	}, [approvalsData, optimisticWorkflowStatus]);

	const approvalRequest = useMemo((): ApprovalRequest | null => {
		if (isDataStale) return null;
		if (isOptimisticallyCancelled) return null;

		const application = approvalsData?.confluence_getActiveWorkflowApplication?.workflowApplication;

		if (optimisticWorkflowStatus && application?.workflowStatusId !== optimisticWorkflowStatus.id) {
			return {
				application: {
					contentId,
					contentVersion,
					contentStatus,
					applicationId: '',
					isActive: true,
					workflowStatusId: optimisticWorkflowStatus.id,
				},
				review: null,
			};
		}

		const review =
			approvalsData?.confluence_getActiveWorkflowApplication?.workflowReviewWithDecisions;
		if (!application) return null;
		return {
			application: {
				contentId: application.contentId || contentId,
				contentVersion: application.contentVersion || 1,
				contentStatus: application.contentStatus || '', // NOTE: This indicates draft (DRAFT) or published (CURRENT)
				applicationId: application.id || '',
				isActive: application.isActive || false,
				workflowStatusId: application.workflowStatusId || '',
				...(application.modifier && {
					applicationLastModifiedBy: {
						displayName: application.modifier.displayName || '',
						avatarUrl: application.modifier.photos?.find((p) => p?.isPrimary)?.value || undefined,
						userId: application.modifier.id || '',
					},
				}),
				applicationLastModifiedDate: application.lastModificationDate?.value || '',
			},
			// There can be an application without a review; return review: null in that case
			review:
				review !== null && review !== undefined
					? {
							reviewId: review.id || '',
							reviewCreationDate: review.creationDate?.value || '',
							requestorId: review.creatorAaid || '',
							requestorName: review.creator?.displayName || '',
							requestorAvatarUrl: review.creator?.photos?.find((p) => p?.isPrimary)?.value || '',
							reviewLastModifiedDate: review.reviewLastModified?.value || '', // milliseconds since epoch
							reviewComment: review.reviewComment || undefined,
							reviewStatus: review.reviewStatus || 'PENDING',
							approvers: review.reviewerDecisions?.reduce<Approver[]>((acc, reviewerDecision) => {
								if (reviewerDecision == null) return acc;
								acc.push({
									decisionId: reviewerDecision.decisionId || '',
									reviewerId: reviewerDecision.reviewerId || '',
									isRequired: reviewerDecision.required ?? true,
									principalId: reviewerDecision.principalId || '',
									principalType: reviewerDecision.principalType || 'USER',
									name: reviewerDecision.user?.displayName || '',
									avatarUrl: reviewerDecision.user?.photos?.find((p) => p?.isPrimary)?.value || '',
									status: reviewerDecision.reviewerDecisionStatus || 'PENDING_REVIEW',
									decisionComment: reviewerDecision.comment || '',
									responseLastModifiedDate: reviewerDecision?.lastModifiedDate?.value || '',
								});
								return acc;
							}, []),
						}
					: null,
		};
	}, [
		isDataStale,
		isOptimisticallyCancelled,
		optimisticWorkflowStatus,
		contentVersion,
		contentStatus,
		approvalsData?.confluence_getActiveWorkflowApplication?.workflowApplication,
		approvalsData?.confluence_getActiveWorkflowApplication?.workflowReviewWithDecisions,
		contentId,
	]);

	const isCurrentUserRequestor = useMemo(() => {
		return approvalRequest?.review?.requestorId === userId;
	}, [approvalRequest, userId]);

	const currentUserApprover = useMemo(() => {
		return (
			approvalRequest?.review?.approvers?.find((approver) => approver.principalId === userId) ||
			null
		);
	}, [approvalRequest, userId]);

	const onWorkflowApplicationChanged = useCallback(
		(
			_eventName: string,
			response: { workflowApplicationChanged?: ConfluenceWorkflowApplicationChanged },
		) => {
			const eventData = response?.workflowApplicationChanged;
			const eventContentStatus = eventData?.contentStatus;
			if (eventContentStatus !== contentStatus) {
				return;
			}
			// Always refetch to pick up the latest data from the server.
			void refetchApprovalsData(refetchVariables);
			// Reset the optimistic cancellation state when a live update arrives from another user.
			// Without this, if User 1 clears the review status (setting isOptimisticallyCancelled=true)
			// and User 2 then adds the review status back, User 1 would never see the new review
			// because isOptimisticallyCancelled would still be true, causing approvalRequest to
			// remain null even after the refetch returns fresh data with the new review.
			//
			// However, skip resetting when a cancellation mutation is in flight — the subscription
			// event may arrive before the mutation completes, meaning the refetch would return stale
			// data that still shows the old workflow status. This causes a flash where the old status
			// briefly reappears before the cancel mutation settles and the correct state is shown.
			// The cancel mutation's own completion path handles clearing isCancellationInFlightRef
			// and triggering a final refetch with up-to-date data.
			if (!isCancellationInFlightRef.current) {
				setIsOptimisticallyCancelled(false);
			}
		},
		[refetchApprovalsData, refetchVariables, contentStatus],
	);

	// Subscribe to real-time approval status changes (from backend workflowApplicationChanged events)
	useSubscriptionEvent({
		subscriberId: 'ApprovalsProviderWorkflowApplicationChanged',
		delta: 'workflowApplicationChanged',
		contentId,
		contentType: contentType || 'page',
		skip: !expValEquals('confluence_approvals_eap', 'isEnabled', true),
		onDataReceived: onWorkflowApplicationChanged,
	});

	// When the other mode's workflow changes (e.g. a DRAFT review is started while viewing the
	// published page), refetch so that otherModeReviewStatus reflects the latest state and any
	// banners or warnings dependent on it update accordingly.
	const onOtherModeWorkflowApplicationChanged = useCallback(
		(
			_eventName: string,
			response: { workflowApplicationChanged?: ConfluenceWorkflowApplicationChanged },
		) => {
			const eventData = response?.workflowApplicationChanged;
			const eventContentStatus = eventData?.contentStatus;
			if (eventContentStatus !== otherContentStatus) {
				return;
			}
			void refetchApprovalsData(refetchVariables);
		},
		[refetchApprovalsData, refetchVariables, otherContentStatus],
	);

	useSubscriptionEvent({
		subscriberId: 'ApprovalsProviderOtherModeWorkflowApplicationChanged',
		delta: 'workflowApplicationChanged',
		contentId,
		contentType: contentType || 'page',
		skip: !expValEquals('confluence_approvals_eap', 'isEnabled', true),
		onDataReceived: onOtherModeWorkflowApplicationChanged,
	});

	const approvalProgress = useMemo(() => {
		if (!approvalRequest) return { current: 0, total: 0 };
		const responded = approvalRequest.review?.approvers?.filter(
			(a) => a.status !== null && a.status !== 'PENDING_REVIEW',
		).length;
		return {
			current: responded ?? 0,
			total: approvalRequest.review?.approvers?.length ?? 0,
		};
	}, [approvalRequest]);

	// GraphQL Mutations
	const [createWorkflowApplicationMutationFn] = useMutationInterop<
		ApprovalsContextCreateWorkflowApplicationMutation$data,
		ApprovalsContextCreateWorkflowApplicationMutation$variables
	>(createWorkflowApplicationMutation, {
		refetchQueries: [{ query: approvalsDataQuery, variables: refetchVariables }],
	});

	const [createWorkflowReviewMutationFn] = useMutationInterop<
		ApprovalsContextCreateWorkflowReviewMutation$data,
		ApprovalsContextCreateWorkflowReviewMutation$variables
	>(createWorkflowReviewMutation, {
		refetchQueries: [{ query: approvalsDataQuery, variables: refetchVariables }],
	});

	const [updateWorkflowApplicationMutationFn] = useMutationInterop<
		ApprovalsContextUpdateWorkflowApplicationMutation$data,
		ApprovalsContextUpdateWorkflowApplicationMutation$variables
	>(updateWorkflowApplicationMutation, {
		refetchQueries: [{ query: approvalsDataQuery, variables: refetchVariables }],
	});

	const [updateReviewerDecisionMutationFn] = useMutationInterop<
		ApprovalsContextUpdateReviewerDecisionMutation$data,
		ApprovalsContextUpdateReviewerDecisionMutation$variables
	>(updateReviewerDecisionMutation, {
		refetchQueries: [{ query: approvalsDataQuery, variables: refetchVariables }],
	});

	const [updateWorkflowReviewMutationFn] = useMutationInterop<
		ApprovalsContextUpdateWorkflowReviewMutation$data,
		ApprovalsContextUpdateWorkflowReviewMutation$variables
	>(updateWorkflowReviewMutation, {
		refetchQueries: [{ query: approvalsDataQuery, variables: refetchVariables }],
	});

	const [removeContentApprovalReviewerMutationFn] = useMutationInterop<
		ApprovalsContextRemoveContentApprovalReviewerMutation$data,
		ApprovalsContextRemoveContentApprovalReviewerMutation$variables
	>(removeContentApprovalReviewerMutation, {
		refetchQueries: [{ query: approvalsDataQuery, variables: refetchVariables }],
	});

	const [resolveApprovalAgentMutationFn] = useMutationInterop<
		{
			confluence_resolveApprovalAgent: {
				success: boolean;
				agent: { id: string } | null;
				errors: { message: string }[];
			} | null;
		},
		{ cloudId: string }
	>(resolveApprovalAgentMutation);

	const [createWorkflowApplicationWithReviewMutationFn] = useMutationInterop<
		ApprovalsContextCreateWorkflowApplicationWithReviewMutation$data,
		ApprovalsContextCreateWorkflowApplicationWithReviewMutation$variables
	>(createWorkflowApplicationWithReviewMutation, {
		refetchQueries: [{ query: approvalsDataQuery, variables: refetchVariables }],
	});

	// Wrapped mutation functions
	// Resolves the approval agent ID. If the mutation fails, returns an empty string
	// so the caller can fall back to invoking regular Rovo instead of the agent.
	const resolveApprovalAgent = useCallback(async (): Promise<string> => {
		try {
			const result = await resolveApprovalAgentMutationFn({ variables: { cloudId } });
			const response = result?.data?.confluence_resolveApprovalAgent;
			if (!response?.success || response.errors?.length > 0) {
				return '';
			}
			return response.agent?.id ?? '';
		} catch {
			return '';
		}
	}, [cloudId, resolveApprovalAgentMutationFn]);

	const createWorkflowApplication = useCallback(
		async (status: WorkflowStatus): Promise<CreateWorkflowApplicationResult> => {
			setIsOptimisticallyCancelled(false);
			setOptimisticWorkflowStatus(status);

			try {
				const result = await createWorkflowApplicationMutationFn({
					variables: {
						cloudId,
						createWorkflowApplicationRequestInput: {
							contentId,
							contentStatus,
							workflowId: 1,
							workflowStatusId: 1, // hardcoded -- backend assigns status on creation
							isActive: true,
						},
					},
				});

				const workflowStatus = result.data?.confluence_createWorkflowApplication?.workflowStatus;
				return {
					applicationId: result.data?.confluence_createWorkflowApplication?.workflowApplication?.id,
					workflowStatusId: workflowStatus?.id ?? '1',
					name: workflowStatus?.displayProperties?.name || '',
					allowsReviews: workflowStatus?.allowsReviews || false,
				};
			} catch (error) {
				setOptimisticWorkflowStatus(null);
				throw error;
			}
		},
		[cloudId, contentId, contentStatus, createWorkflowApplicationMutationFn],
	);

	/** Variables are optional: if not provided, this will cancel the workflow application for
	the latest content version for the current content status */
	const cancelWorkflowApplication = useCallback(
		async (variables?: Omit<UpdateWorkflowApplicationVariables, 'isActive'>): Promise<void> => {
			closeRightSidebar(APPROVALS_PANE_ID);
			isCancellationInFlightRef.current = true;

			try {
				await updateWorkflowApplicationMutationFn({
					variables: {
						cloudId,
						updateWorkflowApplicationRequestInput: {
							contentId: variables?.contentId
								? parseInt(variables.contentId, 10)
								: parseInt(contentId, 10),
							contentStatus: (variables?.contentStatus ?? contentStatus) as GraphQLContentStatus,
							version:
								variables?.version ??
								(contentStatus === CURRENT_CONTENT_STATUS ? contentVersion : 1),
							workflowStatusId: parseInt(
								variables?.workflowStatusId ??
									approvalRequest?.application?.workflowStatusId ??
									'1',
								10,
							),
							isActive: false,
							workflowId: 1,
						},
					},
				});
			} catch (error) {
				setIsOptimisticallyCancelled(false);
				markErrorAsHandled(error);
				// TODO: Add error handling
			} finally {
				isCancellationInFlightRef.current = false;
			}
		},
		[
			cloudId,
			contentId,
			contentStatus,
			contentVersion,
			approvalRequest?.application?.workflowStatusId,
			updateWorkflowApplicationMutationFn,
			closeRightSidebar,
		],
	);

	const clearApprovalRequestOptimistically = useCallback(() => {
		setIsOptimisticallyCancelled(true);
		setOptimisticWorkflowStatus(null);
	}, []);

	const requestLiveDocApprovalConversion = useCallback((requestContentId: string) => {
		if (!fg('confluence_approvals_beta')) return;
		setLiveDocApprovalRedirectIntent({ contentId: requestContentId });
		setPendingLiveDocApprovalContentId(requestContentId);
	}, []);

	const clearLiveDocApprovalConversion = useCallback(() => {
		if (!fg('confluence_approvals_beta')) return;
		setPendingLiveDocApprovalContentId(null);
	}, []);

	const startApproval = useCallback(
		async (variables: StartApprovalVariables) => {
			const result = await createWorkflowReviewMutationFn({
				variables: {
					cloudId,
					createContentApprovalRequestInput: {
						contentId,
						contentStatus,
						version: contentStatus === CURRENT_CONTENT_STATUS ? contentVersion : 1, // DRAFT version is always 1,
						comment: variables.message,
						reviewers: variables.approvers,
					},
				},
			});

			const response = result.data?.confluence_createContentApproval;
			if (response && !response.success) {
				handleApprovalMutationError(response.errors, 'Failed to start approval');
			}

			return response?.contentApproval ?? undefined;
		},
		[cloudId, contentId, contentStatus, contentVersion, createWorkflowReviewMutationFn],
	);

	const updateApprovalStatus = useCallback(
		async (variables: UpdateApprovalStatusVariables) => {
			const decisionStatus = variables.status;

			const result = await updateReviewerDecisionMutationFn({
				variables: {
					cloudId,
					confluenceUpdateReviewerDecisionInput: {
						reviewerId: parseInt(variables.reviewerId, 10),
						decisionStatus,
						comment: variables.comment || null,
					},
				},
			});

			const response = result.data?.confluence_updateReviewerDecision;
			if (response && !response.success) {
				const errorMessage = response.errors?.[0]?.message || 'Failed to update reviewer decision';
				throw new Error(errorMessage);
			}
		},
		[cloudId, updateReviewerDecisionMutationFn],
	);

	const addApprover = useCallback(
		async (variables: AddApproverVariables) => {
			if (!approvalRequest?.application || !approvalRequest?.review) {
				throw new Error('approval request with review is required');
			}

			const existingReviewers = approvalRequest.review.approvers.map((a) => ({
				principalId: a.principalId,
				principalType: (a.principalType === 'GROUP' ? 'GROUP' : 'USER') as PrincipalType,
				isRequired: a.isRequired,
			}));
			const existingIds = new Set(existingReviewers.map((r) => r.principalId));
			const mergedReviewers = [...existingReviewers];
			for (const r of variables.approvers) {
				if (!existingIds.has(r.principalId)) {
					mergedReviewers.push({
						principalId: r.principalId,
						principalType: r.principalType,
						isRequired: r.isRequired,
					});
					existingIds.add(r.principalId);
				}
			}

			await updateWorkflowReviewMutationFn({
				variables: {
					cloudId,
					updateContentApprovalGraphQLInput: {
						workflowApplicationId: parseInt(approvalRequest.application.applicationId, 10),
						contentApprovalReviewId: parseInt(approvalRequest.review.reviewId, 10),
						reviewers: mergedReviewers.map((a) => ({
							principalId: a.principalId,
							principalType: a.principalType === 'GROUP' ? 'GROUP' : 'USER',
							isRequired: a.isRequired,
						})),
					},
				},
			});
		},
		[approvalRequest, cloudId, updateWorkflowReviewMutationFn],
	);

	const removeApprover = useCallback(
		async (variables: RemoveApproverVariables) => {
			if (!approvalRequest?.application || !approvalRequest?.review) {
				throw new Error('approval request with review is required');
			}

			const approver = approvalRequest.review.approvers.find(
				(a) => a.principalId === variables.principalId,
			);
			if (!approver) {
				throw new Error('Approver not found in current review');
			}

			await removeContentApprovalReviewerMutationFn({
				variables: {
					cloudId,
					removeContentApprovalReviewerInput: {
						workflowApplicationId: approvalRequest.application.applicationId,
						contentApprovalReviewId: approvalRequest.review.reviewId,
						principalId: variables.principalId,
						principalType: approver.principalType,
					},
				},
			});
		},
		[approvalRequest, cloudId, removeContentApprovalReviewerMutationFn],
	);

	const cancelApproval = useCallback(
		async (variables: CancelApprovalVariables) => {
			closeRightSidebar(APPROVALS_PANE_ID);

			await updateWorkflowReviewMutationFn({
				variables: {
					cloudId,
					updateContentApprovalGraphQLInput: {
						workflowApplicationId: parseInt(variables.workflowApplicationId, 10),
						contentApprovalReviewId: parseInt(variables.contentApprovalReviewId, 10),
						approvalStatus: 'CANCELED',
					},
				},
			});
		},
		[cloudId, updateWorkflowReviewMutationFn, closeRightSidebar],
	);

	const startApprovalCombined = useCallback(
		async (variables: StartApprovalVariables): Promise<StartApprovalResult | undefined> => {
			const result = await createWorkflowApplicationWithReviewMutationFn({
				variables: {
					cloudId,
					createWorkflowApplicationWithReviewRequestInput: {
						contentId,
						contentStatus,
						version: contentStatus === CURRENT_CONTENT_STATUS ? contentVersion : 1,
						comment: variables.message,
						reviewers: variables.approvers,
					},
				},
			});

			const response = result.data?.confluence_createWorkflowApplicationWithReview;
			if (response && !response.success) {
				handleApprovalMutationError(response.errors, 'Failed to start approval');
			}

			return {
				id: response?.workflowReviewWithDecisions?.id ?? '',
				reviewStatus: 'PENDING' as ConfluenceWorkflowReviewStatus,
				workflowApplicationId: response?.workflowApplication?.id ?? null,
				comment: variables.message ?? null,
			};
		},
		[
			cloudId,
			contentId,
			contentStatus,
			contentVersion,
			createWorkflowApplicationWithReviewMutationFn,
		],
	);

	const otherModeReviewStatus = useMemo((): ApprovalStatus | null => {
		if (isDataStale) return null;
		return (
			approvalsData?.confluence_getActiveWorkflowApplicationForOtherStatus
				?.workflowReviewWithDecisions?.reviewStatus ?? null
		);
	}, [isDataStale, approvalsData?.confluence_getActiveWorkflowApplicationForOtherStatus]);

	const isDraftLockedForEditing = useMemo(() => {
		if (contentStatus !== DRAFT_CONTENT_STATUS) return false;
		const review = approvalRequest?.review;
		if (review == null) return false;
		return review.reviewStatus === 'PENDING' || review.reviewStatus === 'APPROVED';
	}, [contentStatus, approvalRequest?.review]);

	// Context values - memoized to prevent unnecessary re-renders
	const contextValue = useMemo<ApprovalsContextValue>(
		() => ({
			approvalRequest,
			// If we already have data for the current page, keep rendering it during in-flight
			// refetches (e.g. after a reviewer decision) so the byline and page banners don't
			// flicker. `isDataStale` / `isAwaitingFreshData` still hide UI on real navigations.
			approvalRequestLoading:
				(approvalsLoading && !approvalsData) || isDataStale || isAwaitingFreshData,
			contentVersion,
			availableWorkflowStatuses,
			contentId,
			contentStatus,
			isCurrentUserRequestor,
			isCurrentUserApprover: !!currentUserApprover,
			currentUserApprover,
			approvalProgress,
			hasEditPermission,
			isDraftLockedForEditing,
			otherModeReviewStatus,
			isContentApprovalsEnabled,
			pendingLiveDocApprovalContentId,
		}),
		[
			approvalRequest,
			approvalsLoading,
			approvalsData,
			isDataStale,
			isAwaitingFreshData,
			contentVersion,
			availableWorkflowStatuses,
			contentId,
			contentStatus,
			isCurrentUserRequestor,
			currentUserApprover,
			approvalProgress,
			hasEditPermission,
			isDraftLockedForEditing,
			otherModeReviewStatus,
			isContentApprovalsEnabled,
			pendingLiveDocApprovalContentId,
		],
	);

	const dispatchValue = useMemo<ApprovalsDispatchContextValue>(
		() => ({
			createWorkflowApplication,
			cancelWorkflowApplication,
			clearApprovalRequestOptimistically,
			startApproval,
			updateApprovalStatus,
			addApprover,
			removeApprover,
			cancelApproval,
			resolveApprovalAgent,
			startApprovalCombined,
			requestLiveDocApprovalConversion,
			clearLiveDocApprovalConversion,
		}),
		[
			createWorkflowApplication,
			cancelWorkflowApplication,
			clearApprovalRequestOptimistically,
			startApproval,
			updateApprovalStatus,
			addApprover,
			removeApprover,
			cancelApproval,
			resolveApprovalAgent,
			startApprovalCombined,
			requestLiveDocApprovalConversion,
			clearLiveDocApprovalConversion,
		],
	);

	return (
		<ApprovalsContext.Provider value={contextValue}>
			<ApprovalsDispatchContext.Provider value={dispatchValue}>
				{children}
				{expValEquals('confluence_approvals_eap', 'isEnabled', true) && (
					<>
						<ApprovalCtaFlag />
						<QuickReloadSubscriber
							name="approvals"
							types={[ReloadType.content]}
							reload={refetchApprovalsData}
						/>
					</>
				)}
			</ApprovalsDispatchContext.Provider>
		</ApprovalsContext.Provider>
	);
};

/**
 * Hook to access approval data (read-only)
 *
 * @example
 * ```tsx
 * const { approvalRequest, loading, approvalProgress } = useApprovals();
 * ```
 */
export const useApprovals = (): ApprovalsContextValue => {
	return useContext(ApprovalsContext);
};

/**
 * Hook to access approval mutations and actions (write-only)
 *
 * @example
 * ```tsx
 * const { startApproval, updateApprovalStatus } = useApprovalsDispatch();
 * ```
 */
export const useApprovalsDispatch = (): ApprovalsDispatchContextValue => {
	return useContext(ApprovalsDispatchContext);
};
